import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Camera, X, CheckCircle, AlertTriangle, Loader2, 
  Car, Shield, Search, Settings, Image as ImageIcon,
  Trash2, Eye, Construction, MapPin, FileDown, Globe, Info,
  Upload, Fuel, Calendar, Tag, Palette, ChevronRight
} from "lucide-react";
import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;

// Country options with currency
const COUNTRIES = [
  { code: "IN", name: "India", currency: "₹", flag: "🇮🇳" },
  { code: "TH", name: "Thailand", currency: "฿", flag: "🇹🇭" },
  { code: "MY", name: "Malaysia", currency: "RM", flag: "🇲🇾" },
  { code: "PH", name: "Philippines", currency: "₱", flag: "🇵🇭" },
];

// Photo categories for each analysis type
const PHOTO_CATEGORIES = {
  dents: [
    { id: "front_bumper", label: "Front Bumper", required: true },
    { id: "rear_bumper", label: "Rear Bumper", required: true },
    { id: "driver_front_door", label: "Driver Front Door", required: true },
    { id: "driver_rear_door", label: "Driver Rear Door", required: true },
    { id: "passenger_front_door", label: "Passenger Front Door", required: true },
    { id: "passenger_rear_door", label: "Passenger Rear Door", required: true },
    { id: "hood", label: "Hood/Bonnet", required: true },
    { id: "trunk", label: "Trunk/Boot", required: true },
    { id: "roof", label: "Roof", required: false },
    { id: "driver_fender", label: "Driver Side Fender", required: false },
    { id: "passenger_fender", label: "Passenger Side Fender", required: false },
  ],
  accident: [
    { id: "front_bumper", label: "Front Bumper", required: true },
    { id: "rear_bumper", label: "Rear Bumper", required: true },
    { id: "driver_front_door", label: "Driver Front Door", required: true },
    { id: "driver_rear_door", label: "Driver Rear Door", required: true },
    { id: "passenger_front_door", label: "Passenger Front Door", required: true },
    { id: "passenger_rear_door", label: "Passenger Rear Door", required: true },
    { id: "hood", label: "Hood/Bonnet", required: true },
    { id: "trunk", label: "Trunk/Boot", required: true },
    { id: "engine_bay", label: "Engine Bay (Hood Up)", required: true },
    { id: "front_left_wheel", label: "Front Left Wheel Area", required: false },
    { id: "front_right_wheel", label: "Front Right Wheel Area", required: false },
    { id: "underbody_front", label: "Underbody Front", required: false },
  ],
  identify: [
    { id: "front_view", label: "Front View", required: true },
    { id: "rear_view", label: "Rear View (with badges)", required: false },
    { id: "side_view", label: "Side View", required: false },
    { id: "badge_closeup", label: "Badge / Emblem Close-up", required: false },
  ],
  verification: [
    { id: "engine_number", label: "Engine Number (On Engine Block)", required: true },
    { id: "chassis_number", label: "Chassis Number (VIN Plate)", required: true },
    { id: "chassis_stamped", label: "Chassis Number (Stamped on Body)", required: true },
    { id: "registration_plate", label: "Registration Plate", required: false },
    { id: "odometer", label: "Odometer Reading", required: false },
  ],
};

const ANALYSIS_TYPES = {
  dents: {
    title: "Dents & Scratch Identification",
    description: "Upload photos of all car body panels to identify dents, scratches, and paint damage",
    icon: Car,
    color: "orange",
    disclaimer: {
      title: "Photo Guidelines for Best Results",
      tips: [
        "Take photos in bright, natural daylight or well-lit environment",
        "Capture close-up shots (within 1-2 feet) of each panel",
        "Avoid blurry, dark, or overexposed images",
        "Ensure the vehicle surface is clean and dry",
        "Take multiple angles if damage is suspected"
      ],
      warning: "AI accuracy depends on image quality. Blurry, distant, or poorly lit photos may result in missed detections or false positives. For official inspections, verify results in a controlled environment."
    }
  },
  accident: {
    title: "Accident Analysis",
    description: "Comprehensive analysis to detect signs of previous accidents or collision repairs",
    icon: Shield,
    color: "red",
    disclaimer: {
      title: "Photo Guidelines for Best Results",
      tips: [
        "Capture close-up photos of panel gaps, bolts, and paint surfaces",
        "Take engine bay photos with hood fully open in good lighting",
        "Photograph bumper corners and fender edges clearly",
        "Avoid shadows, reflections, and camera glare",
        "Include comparison shots of both sides of the vehicle"
      ],
      warning: "Accident detection requires high-quality, close-up images. Distant or unclear photos may miss subtle repair signs. Results should be verified by a professional inspector for critical decisions."
    }
  },
  identify: {
    title: "Vehicle Identifier",
    description: "Upload car photos to identify manufacturer, model, fuel type, and estimated year",
    icon: Search,
    color: "purple",
    disclaimer: {
      title: "Photo Tips for Best Identification",
      tips: [
        "Front view clearly showing grille, headlights, and logo",
        "Rear view capturing all badges, model name, and fuel type indicators",
        "Side profile showing full body shape",
        "Close-up of any model/variant badges on trunk or fenders"
      ],
      warning: "Identification accuracy depends on photo clarity. Rear badge close-ups significantly improve fuel type detection. Year estimation is based on design generation (±2-3 years)."
    }
  },
  verification: {
    title: "Engine & Chassis Verification",
    description: "Verify engine and chassis numbers for authenticity and tampering detection",
    icon: Settings,
    color: "emerald",
    disclaimer: {
      title: "Photo Guidelines for Best Results",
      tips: [
        "Capture engine number stamp at close range (under 6 inches)",
        "Ensure all characters are in sharp focus",
        "Clean any dirt or grease from the stamped area before photographing",
        "Take VIN plate photo straight-on to avoid angle distortion",
        "Include surrounding area to show surface condition"
      ],
      warning: "Number verification requires crystal-clear, high-resolution images. Blurry or angled photos may result in incorrect readings. Always cross-verify with official documents."
    }
  },
};

function PhotoUploader({ category, photos, onPhotoAdd, onPhotoRemove, disabled }) {
  const photo = photos[category.id];
  const inputId = `photo-${category.id}`;

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onPhotoAdd(category.id, file);
    }
  };

  return (
    <div className={`p-4 border rounded-lg transition-all ${
      photo ? 'border-emerald-200 bg-emerald-50/50' : 
      category.required ? 'border-orange-200 bg-orange-50/30' : 'border-slate-200 bg-slate-50/50'
    }`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-700">{category.label}</span>
          {category.required && <Badge className="text-xs bg-orange-100 text-orange-700 border-orange-200">Required</Badge>}
        </div>
        {photo && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPhotoRemove(category.id)}
            className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
            disabled={disabled}
          >
            <X size={14} />
          </Button>
        )}
      </div>
      
      {photo ? (
        <div className="relative">
          <img
            src={URL.createObjectURL(photo)}
            alt={category.label}
            className="w-full h-32 object-cover rounded-md"
          />
          <div className="absolute top-2 right-2">
            <CheckCircle size={20} className="text-emerald-500 bg-white rounded-full" />
          </div>
        </div>
      ) : (
        <label
          htmlFor={inputId}
          className={`flex flex-col items-center justify-center h-32 border-2 border-dashed rounded-md cursor-pointer transition-colors ${
            disabled ? 'border-slate-200 bg-slate-100 cursor-not-allowed' : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'
          }`}
        >
          <Camera size={24} className="text-slate-400 mb-2" />
          <span className="text-sm text-slate-500">Click to upload</span>
          <input
            id={inputId}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            disabled={disabled}
          />
        </label>
      )}
    </div>
  );
}

function AnalysisResultCard({ result, type }) {
  const [isExporting, setIsExporting] = useState(false);
  
  if (!result) return null;
  
  const config = ANALYSIS_TYPES[type];
  const Icon = config.icon;
  const analysisId = result.analysis_id;
  
  // Handle different result structures
  const issues = result.issues || result.evidence_found || [];
  const hasAccidentData = result.accident_likelihood || result.panel_color_analysis;
  const hasVerificationData = result.verification;
  
  const handleExportPdf = async () => {
    setIsExporting(true);
    try {
      const response = await api.exportVisionPdf(type, result);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `wisedrive_vision_${type}_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      toast.error("Failed to export PDF");
    } finally {
      setIsExporting(false);
    }
  };
  
  return (
    <Card className="border border-slate-200 shadow-none rounded-sm overflow-hidden mt-6" data-testid="vision-result-card">
      <div className="border-b border-slate-200 p-4 bg-slate-900 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={18} className="text-orange-400" />
          <span className="text-sm font-bold uppercase tracking-wider text-white">
            {config.title} - AI Analysis Results
          </span>
          {analysisId && (
            <span className="text-xs font-mono text-slate-400 ml-2">
              ID: {analysisId.substring(0, 8)}...
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {result.status !== "error" && (
            <Button
              size="sm"
              variant="ghost"
              onClick={handleExportPdf}
              disabled={isExporting}
              className="h-8 px-3 text-orange-400 hover:text-orange-300 hover:bg-slate-800"
              data-testid="export-vision-pdf"
            >
              {isExporting ? (
                <Loader2 size={14} className="mr-1 animate-spin" />
              ) : (
                <FileDown size={14} className="mr-1" />
              )}
              Export PDF
            </Button>
          )}
          {result.ai_providers_used && (
            <Badge className="text-xs bg-emerald-600 text-white">
              AI Analysis ✓
            </Badge>
          )}
        </div>
      </div>
      <CardContent className="p-6">
        {/* Analysis ID Reference Bar */}
        {analysisId && result.status !== "error" && (
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Analysis Reference ID:</span>
              <code className="text-xs font-mono bg-white px-2 py-1 rounded border border-slate-200 text-slate-700 select-all" data-testid="result-analysis-id">
                {analysisId}
              </code>
            </div>
            <button 
              onClick={() => {
                navigator.clipboard.writeText(analysisId);
                toast.success("Analysis ID copied!");
              }}
              className="text-xs text-slate-500 hover:text-slate-700 underline"
            >
              Copy ID
            </button>
          </div>
        )}
        {result.status === "error" ? (
          <div className="text-center py-8">
            <AlertTriangle size={36} className="mx-auto text-red-400 mb-3" />
            <p className="text-base text-red-500">{result.error || "Analysis failed"}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Cache Indicator */}
            {result.from_cache && (
              <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-md text-sm text-emerald-700">
                <CheckCircle size={16} className="text-emerald-500" />
                <span><strong>Instant Result:</strong> These images were analyzed before. Showing cached result for consistency.</span>
              </div>
            )}
            
            {/* Overall Assessment */}
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Overall Assessment</p>
              <p className="text-base text-slate-700 leading-relaxed">{result.summary}</p>
            </div>

            {/* Severity & Stats Row */}
            <div className="flex flex-wrap items-center gap-4">
              {result.severity && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-slate-600">Condition:</span>
                  <Badge className={`text-sm px-3 py-1 ${
                    result.severity === 'good' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' :
                    result.severity === 'fair' ? 'bg-amber-100 text-amber-800 border-amber-200' :
                    result.severity === 'poor' ? 'bg-orange-100 text-orange-800 border-orange-200' :
                    'bg-red-100 text-red-800 border-red-200'
                  }`}>
                    {result.severity.toUpperCase()}
                  </Badge>
                </div>
              )}
              {result.accident_likelihood && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-slate-600">Accident Likelihood:</span>
                  <Badge className={`text-sm px-3 py-1 ${
                    result.accident_likelihood === 'none' ? 'bg-emerald-100 text-emerald-800' :
                    result.accident_likelihood === 'possible' ? 'bg-amber-100 text-amber-800' :
                    result.accident_likelihood === 'likely' ? 'bg-orange-100 text-orange-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {result.accident_likelihood.toUpperCase()}
                  </Badge>
                </div>
              )}
              
              {/* Car Color Detected */}
              {result.car_color_detected && (
                <div className="flex items-center gap-2 px-3 py-1 bg-slate-100 rounded-full border border-slate-200">
                  <span className="text-sm text-slate-600">🚗 Car Color:</span>
                  <span className="text-sm font-semibold text-slate-800">{result.car_color_detected}</span>
                </div>
              )}
              
              {result.total_dents_found !== undefined && (
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 rounded-full border border-blue-200">
                    <span className="text-blue-600 font-bold">□</span>
                    <span className="text-sm text-slate-700">Dents: <strong className="text-blue-700">{result.total_dents_found}</strong></span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1 bg-purple-50 rounded-full border border-purple-200">
                    <span className="text-purple-600 font-bold">✕</span>
                    <span className="text-sm text-slate-700">Scratches: <strong className="text-purple-700">{result.total_scratches_found || 0}</strong></span>
                  </div>
                  {result.total_other_damage > 0 && (
                    <div className="flex items-center gap-2 px-3 py-1 bg-orange-50 rounded-full border border-orange-200">
                      <span className="text-orange-600 font-bold">◇</span>
                      <span className="text-sm text-slate-700">Other: <strong className="text-orange-700">{result.total_other_damage}</strong></span>
                    </div>
                  )}
                </div>
              )}
              {result.tampering_detected !== undefined && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-slate-600">Tampering:</span>
                  <Badge className={`text-sm px-3 py-1 ${
                    result.tampering_detected ? 'bg-red-100 text-red-800' : 'bg-emerald-100 text-emerald-800'
                  }`}>
                    {result.tampering_detected ? 'DETECTED' : 'NOT DETECTED'}
                  </Badge>
                </div>
              )}
            </div>

            {/* Accident Analysis - Panel Color Analysis */}
            {hasAccidentData && result.panel_color_analysis && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                  <p className="text-xs font-bold uppercase tracking-widest text-emerald-700 mb-2">Matching Panels (Original)</p>
                  <div className="flex flex-wrap gap-2">
                    {result.panel_color_analysis.matching_panels?.map((panel, i) => (
                      <Badge key={i} className="bg-emerald-100 text-emerald-800 border-emerald-200">{panel}</Badge>
                    ))}
                  </div>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-100">
                  <p className="text-xs font-bold uppercase tracking-widest text-orange-700 mb-2">Mismatched Panels (Repainted/Replaced)</p>
                  <div className="flex flex-wrap gap-2">
                    {result.panel_color_analysis.mismatched_panels?.map((panel, i) => (
                      <Badge key={i} className="bg-orange-100 text-orange-800 border-orange-200">{panel}</Badge>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Accident Analysis - Fastener Analysis */}
            {hasAccidentData && result.fastener_analysis && (
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Engine Bay Fastener Analysis</p>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm text-slate-600">OEM Condition:</span>
                  <Badge className={`text-sm ${
                    result.fastener_analysis.oem_condition === 'All OEM' ? 'bg-emerald-100 text-emerald-800' :
                    result.fastener_analysis.oem_condition === 'Some replaced' ? 'bg-amber-100 text-amber-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {result.fastener_analysis.oem_condition}
                  </Badge>
                </div>
                {result.fastener_analysis.suspicious_areas?.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-bold text-slate-500">Suspicious Areas:</p>
                    {result.fastener_analysis.suspicious_areas.map((area, i) => (
                      <p key={i} className="text-sm text-orange-700 flex items-center gap-2">
                        <AlertTriangle size={14} className="text-orange-500" />
                        {area}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Verification Results - Enhanced */}
            {hasVerificationData && (
              <div className="space-y-4">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Number Verification Details</p>
                
                {/* Engine Number */}
                {result.verification.engine_number && (
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-bold text-slate-700">Engine Number</span>
                      <Badge className={`text-sm ${
                        result.verification.engine_number.status === 'genuine' ? 'bg-emerald-100 text-emerald-800' :
                        result.verification.engine_number.status === 'suspicious' ? 'bg-amber-100 text-amber-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {result.verification.engine_number.status?.toUpperCase()}
                      </Badge>
                    </div>
                    <p className="text-2xl font-mono font-bold text-slate-900 mb-4">{result.verification.engine_number.number}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div><span className="text-slate-500">Font Analysis:</span> <span className="text-slate-700">{result.verification.engine_number.font_analysis}</span></div>
                      <div><span className="text-slate-500">Spacing:</span> <span className="text-slate-700">{result.verification.engine_number.spacing_analysis}</span></div>
                      <div><span className="text-slate-500">Depth:</span> <span className="text-slate-700">{result.verification.engine_number.depth_analysis}</span></div>
                      <div><span className="text-slate-500">Surface:</span> <span className="text-slate-700">{result.verification.engine_number.surface_condition}</span></div>
                    </div>
                    {result.verification.engine_number.notes && (
                      <p className="text-xs text-slate-500 mt-3 p-2 bg-white rounded border border-slate-200">{result.verification.engine_number.notes}</p>
                    )}
                  </div>
                )}

                {/* Chassis Number */}
                {result.verification.chassis_number && (
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-bold text-slate-700">Chassis Number (VIN)</span>
                      <div className="flex items-center gap-2">
                        {result.verification.chassis_number.hologram_present && (
                          <Badge className="bg-purple-100 text-purple-800">Hologram ✓</Badge>
                        )}
                        <Badge className={`text-sm ${
                          result.verification.chassis_number.status === 'genuine' ? 'bg-emerald-100 text-emerald-800' :
                          result.verification.chassis_number.status === 'suspicious' ? 'bg-amber-100 text-amber-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {result.verification.chassis_number.status?.toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-2xl font-mono font-bold text-slate-900 mb-4">{result.verification.chassis_number.number}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div><span className="text-slate-500">Plate Condition:</span> <span className="text-slate-700">{result.verification.chassis_number.plate_condition}</span></div>
                      <div><span className="text-slate-500">Stamped Match:</span> <span className="text-slate-700">{result.verification.chassis_number.stamped_match}</span></div>
                      <div><span className="text-slate-500">Font Analysis:</span> <span className="text-slate-700">{result.verification.chassis_number.font_analysis}</span></div>
                      <div><span className="text-slate-500">Spacing:</span> <span className="text-slate-700">{result.verification.chassis_number.spacing_analysis}</span></div>
                    </div>
                    {result.verification.chassis_number.notes && (
                      <p className="text-xs text-slate-500 mt-3 p-2 bg-white rounded border border-slate-200">{result.verification.chassis_number.notes}</p>
                    )}
                  </div>
                )}

                {/* Genuineness Factors */}
                {result.genuineness_factors?.length > 0 && (
                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                    <p className="text-xs font-bold uppercase tracking-widest text-emerald-700 mb-2">Factors Supporting Authenticity</p>
                    <ul className="space-y-1">
                      {result.genuineness_factors.map((factor, i) => (
                        <li key={i} className="text-sm text-emerald-800 flex items-start gap-2">
                          <CheckCircle size={14} className="mt-0.5 text-emerald-600 flex-shrink-0" />
                          {factor}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Issues/Evidence Found */}
            {issues.length > 0 && (
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
                  {hasAccidentData ? 'Evidence Found' : 'Issues Identified'}
                </p>
                <div className="space-y-3">
                  {issues.map((issue, i) => {
                    // Determine issue type for styling
                    const issueType = issue.type?.toLowerCase() || 
                      (issue.title?.toLowerCase().includes('scratch') ? 'scratch' : 
                       issue.title?.toLowerCase().includes('dent') ? 'dent' : 'other');
                    
                    const typeStyles = {
                      scratch: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200', icon: '✕' },
                      dent: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200', icon: '□' },
                      paint_chip: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200', icon: '◇' },
                      scuff: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200', icon: '○' },
                      crack: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200', icon: '⚡' },
                      misalignment: { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-200', icon: '⬌' },
                      paint_damage: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200', icon: '◆' },
                      chip: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200', icon: '○' },
                      other: { bg: 'bg-slate-100', text: 'text-slate-800', border: 'border-slate-200', icon: '•' }
                    };
                    
                    const typeStyle = typeStyles[issueType] || typeStyles.other;
                    
                    // Confidence colors
                    const confidenceColors = {
                      high: 'bg-green-100 text-green-800',
                      medium: 'bg-yellow-100 text-yellow-800',
                      low: 'bg-gray-100 text-gray-600'
                    };
                    
                    // Severity colors (updated for new values)
                    const severityColors = {
                      critical: 'bg-red-100 text-red-800',
                      severe: 'bg-red-100 text-red-800',
                      high: 'bg-orange-100 text-orange-800',
                      moderate: 'bg-amber-100 text-amber-800',
                      medium: 'bg-amber-100 text-amber-800',
                      minor: 'bg-sky-100 text-sky-800',
                      low: 'bg-sky-100 text-sky-800'
                    };
                    
                    return (
                      <div key={i} className="p-4 bg-amber-50/50 border border-amber-100 rounded-lg">
                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            {/* Issue type badge */}
                            <Badge className={`text-xs ${typeStyle.bg} ${typeStyle.text} ${typeStyle.border} font-semibold`}>
                              {typeStyle.icon} {issueType.replace('_', ' ').toUpperCase()}
                            </Badge>
                            <span className="text-sm font-bold text-slate-800">{issue.title}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Confidence badge */}
                            {issue.confidence && (
                              <Badge className={`text-xs ${confidenceColors[issue.confidence] || confidenceColors.medium}`}>
                                {issue.confidence} confidence
                              </Badge>
                            )}
                            {/* Severity badge */}
                            <Badge className={`text-xs ${severityColors[issue.severity] || severityColors.medium}`}>
                              {issue.severity}
                            </Badge>
                          </div>
                        </div>
                        
                        {/* Size estimate */}
                        {issue.estimated_size && (
                          <p className="text-xs text-slate-600 mb-2 flex items-center gap-1">
                            <span className="font-semibold">Size:</span> {issue.estimated_size}
                          </p>
                        )}
                        
                        <p className="text-sm text-slate-600 mb-2">{issue.description}</p>
                        
                        {/* Detection method */}
                        {issue.detection_method && (
                          <p className="text-xs text-indigo-600 mb-2 p-2 bg-indigo-50 rounded">
                            🔍 Detection: {issue.detection_method}
                          </p>
                        )}
                        
                        {issue.location && (
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <MapPin size={12} /> Location: {issue.location}
                          </p>
                        )}
                        {issue.marked_area && (
                          <p className="text-xs text-blue-600 mt-1 p-2 bg-blue-50 rounded">
                            📍 Marked Area: {issue.marked_area}
                          </p>
                        )}
                        {issue.accident_indicator && (
                          <p className="text-xs text-orange-700 mt-2 font-semibold">
                            → {issue.accident_indicator}
                          </p>
                        )}
                        {(issue.estimated_cost || issue.repair_method) && (
                          <div className="flex items-center gap-4 mt-2 pt-2 border-t border-amber-200 flex-wrap">
                            {issue.repair_method && (
                              <span className="text-xs text-slate-500">Method: <strong>{issue.repair_method}</strong></span>
                            )}
                            {issue.estimated_cost && (
                              <span className="text-sm text-orange-600 font-mono font-bold">
                                Est: {issue.estimated_cost}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Recommendations */}
            {result.recommendations && result.recommendations.length > 0 && (
              <div className="p-4 bg-sky-50 rounded-lg border border-sky-100">
                <p className="text-xs font-bold uppercase tracking-widest text-sky-700 mb-3">Recommendations</p>
                <ul className="space-y-2">
                  {result.recommendations.map((rec, i) => (
                    <li key={i} className="text-sm text-slate-700 flex items-start gap-3">
                      <CheckCircle size={16} className="mt-0.5 text-sky-500 flex-shrink-0" />
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Inspection Notes */}
            {result.inspection_notes && (
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-600 mb-2">Inspection Notes</p>
                <p className="text-sm text-slate-700">{result.inspection_notes}</p>
              </div>
            )}

            {/* Annotated Images - Visual Proof */}
            {result.annotated_images && result.annotated_images.length > 0 && (
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4">
                  Analyzed Images {result.annotated_images.some(img => img.issues_marked > 0) ? '- Issues Marked' : '- No Issues Detected'}
                </p>
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                  {result.annotated_images.map((img, i) => (
                    <div key={i} className="group border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
                      <div className="p-3 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200 flex items-center justify-between">
                        <span className="text-sm font-semibold text-slate-700">
                          {img.key.replace('photo_', '').replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                        </span>
                        {img.issues_marked > 0 ? (
                          <Badge className="text-xs bg-orange-100 text-orange-700 border-orange-200 font-semibold">
                            {img.issues_marked} {img.issues_marked === 1 ? 'issue' : 'issues'}
                          </Badge>
                        ) : (
                          <Badge className="text-xs bg-green-100 text-green-700 border-green-200 font-semibold">
                            No issues
                          </Badge>
                        )}
                      </div>
                      <div className="relative aspect-[4/3] bg-slate-100">
                        <img 
                          src={`data:image/jpeg;base64,${img.annotated_base64}`}
                          alt={`Analyzed ${img.key}`}
                          className="w-full h-full object-contain"
                          data-testid={`annotated-image-${i}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function VisionAnalysisTab({ type }) {
  const [photos, setPhotos] = useState({});
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [country, setCountry] = useState("IN");
  const [currentJobId, setCurrentJobId] = useState(null); // Track job ID for debugging/support
  const [completedJobId, setCompletedJobId] = useState(null); // Keep ID after analysis completes
  
  const config = ANALYSIS_TYPES[type];
  const categories = PHOTO_CATEGORIES[type];
  const Icon = config.icon;
  const selectedCountry = COUNTRIES.find(c => c.code === country);

  const requiredPhotos = categories.filter(c => c.required);
  const uploadedRequired = requiredPhotos.filter(c => photos[c.id]);
  const canAnalyze = uploadedRequired.length === requiredPhotos.length;
  const totalUploaded = Object.keys(photos).length;

  const handlePhotoAdd = (categoryId, file) => {
    setPhotos(prev => ({ ...prev, [categoryId]: file }));
  };

  const handlePhotoRemove = (categoryId) => {
    setPhotos(prev => {
      const newPhotos = { ...prev };
      delete newPhotos[categoryId];
      return newPhotos;
    });
  };

  const handleClearAll = () => {
    setPhotos({});
    setResult(null);
    setCurrentJobId(null);
    setCompletedJobId(null);
  };

  const handleAnalyze = useCallback(async () => {
    if (!canAnalyze) {
      toast.error("Please upload all required photos");
      return;
    }

    setIsAnalyzing(true);
    setResult(null);

    const formData = new FormData();
    formData.append("analysis_type", type);
    formData.append("country", country);
    
    Object.entries(photos).forEach(([categoryId, file]) => {
      formData.append(`photo_${categoryId}`, file);
    });

    try {
      // Submit analysis job
      const submitResponse = await api.submitVision(formData);
      const { job_id: jobId, from_cache, result: cachedResult, status } = submitResponse.data;
      
      if (!jobId) {
        throw new Error("No job ID received");
      }
      
      setCurrentJobId(jobId); // Store job ID for display
      
      // Check if result came from cache (instant)
      if (from_cache && status === "completed" && cachedResult) {
        setCompletedJobId(jobId);
        setResult({ ...cachedResult, analysis_id: jobId, from_cache: true });
        toast.success("Instant result from cache!", { duration: 3000 });
        return;
      }
      
      toast.info(`Analysis ID: ${jobId.substring(0, 8)}... Processing (30-60 sec)`, { duration: 5000 });
      
      // Poll for results with progress updates
      let attempts = 0;
      const maxAttempts = 60; // 5 minutes max (5s intervals)
      
      const pollForResults = async () => {
        while (attempts < maxAttempts) {
          attempts++;
          
          // Show progress every 15 seconds
          if (attempts === 3) {
            toast.info("Still analyzing... AI is examining your images", { duration: 4000 });
          } else if (attempts === 6) {
            toast.info("Almost done... Generating annotated images", { duration: 4000 });
          }
          
          try {
            const statusResponse = await api.getVisionStatus(jobId);
            const status = statusResponse.data;
            
            if (status.status === "completed") {
              // Store job ID and add it to result for reference
              setCompletedJobId(jobId);
              setResult({ ...status.result, analysis_id: jobId });
              toast.success("Analysis complete!");
              return;
            } else if (status.status === "failed") {
              throw new Error(status.error || "Analysis failed");
            }
            
            // Still processing, wait and try again
            await new Promise(resolve => setTimeout(resolve, 5000));
          } catch (pollErr) {
            // Network errors during polling - retry silently
            if (pollErr.code === 'ECONNABORTED' || pollErr.message?.includes('timeout')) {
              // Timeout on poll request - just retry
              await new Promise(resolve => setTimeout(resolve, 3000));
              continue;
            }
            if (pollErr.response?.status === 404) {
              // Job not found yet, wait a bit and retry
              await new Promise(resolve => setTimeout(resolve, 2000));
              continue;
            }
            throw pollErr;
          }
        }
        
        throw new Error("Analysis timed out. Please try again.");
      };
      
      await pollForResults();
      
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Analysis failed";
      toast.error(msg);
      setResult({ status: "error", error: msg });
    } finally {
      setIsAnalyzing(false);
    }
  }, [photos, type, canAnalyze, country]);

  const handleLoadSample = async () => {
    setIsAnalyzing(true);
    try {
      const response = await axios.get(`${BASE}/api/v1/vs/${type}`);
      setResult(response.data);
      toast.success("Sample report loaded!");
    } catch (err) {
      toast.error("Failed to load sample data");
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className={`w-14 h-14 rounded-lg flex items-center justify-center bg-${config.color}-100`}>
            <Icon size={28} className={`text-${config.color}-600`} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">{config.title}</h2>
            <p className="text-base text-slate-500 mt-1">{config.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Country Selector */}
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger className="w-[160px] h-9 text-sm" data-testid="country-selector">
              <Globe size={14} className="mr-1" />
              <SelectValue>
                {selectedCountry?.flag} {selectedCountry?.name}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {COUNTRIES.map(c => (
                <SelectItem key={c.code} value={c.code}>
                  {c.flag} {c.name} ({c.currency})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLoadSample}
            disabled={isAnalyzing}
            className="text-purple-600 border-purple-200 hover:bg-purple-50"
          >
            <Eye size={14} className="mr-1" /> View Sample Report
          </Button>
          <Badge className="text-sm bg-slate-100 text-slate-700 border-slate-200 px-3 py-1">
            {totalUploaded}/{categories.length} Photos
          </Badge>
          {totalUploaded > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearAll}
              className="text-red-600 border-red-200 hover:bg-red-50"
              disabled={isAnalyzing}
            >
              <Trash2 size={14} className="mr-1" /> Clear All
            </Button>
          )}
        </div>
      </div>

      {/* Photo Quality Disclaimer */}
      {config.disclaimer && (
        <Card className="border border-amber-200 bg-amber-50/50 shadow-none rounded-sm" data-testid="photo-disclaimer">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Info size={18} className="text-amber-600" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-bold text-amber-800 mb-2">{config.disclaimer.title}</h4>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 mb-3">
                  {config.disclaimer.tips.map((tip, i) => (
                    <li key={i} className="text-xs text-amber-700 flex items-start gap-2">
                      <CheckCircle size={12} className="text-amber-500 mt-0.5 flex-shrink-0" />
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex items-start gap-2 p-2 bg-amber-100/50 rounded border border-amber-200">
                  <AlertTriangle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-800 leading-relaxed">{config.disclaimer.warning}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Photo Grid */}
      <Card className="border border-slate-200 shadow-none rounded-sm">
        <CardContent className="p-5">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {categories.map((category) => (
              <PhotoUploader
                key={category.id}
                category={category}
                photos={photos}
                onPhotoAdd={handlePhotoAdd}
                onPhotoRemove={handlePhotoRemove}
                disabled={isAnalyzing}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Analyze Button */}
      <Card className="border border-slate-200 shadow-none rounded-sm">
        <CardContent className="p-5">
          {/* Analysis ID Display - Show during analysis OR after completion */}
          {(currentJobId || completedJobId) && (
            <div className={`border rounded-sm p-3 mb-4 flex items-center justify-between ${
              isAnalyzing 
                ? 'bg-blue-50 border-blue-200' 
                : 'bg-emerald-50 border-emerald-200'
            }`}>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-bold uppercase tracking-wider ${
                  isAnalyzing ? 'text-blue-600' : 'text-emerald-600'
                }`}>
                  {isAnalyzing ? 'Processing' : 'Completed'} - Analysis ID:
                </span>
                <code className="text-xs font-mono bg-white px-2 py-1 rounded border border-slate-200 text-slate-700 select-all" data-testid="vision-analysis-id">
                  {currentJobId || completedJobId}
                </code>
              </div>
              <button 
                onClick={() => navigator.clipboard.writeText(currentJobId || completedJobId)}
                className={`text-xs underline ${
                  isAnalyzing ? 'text-blue-500 hover:text-blue-700' : 'text-emerald-500 hover:text-emerald-700'
                }`}
              >
                Copy ID
              </button>
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-slate-600">
                {canAnalyze 
                  ? `Ready to analyze ${totalUploaded} photos` 
                  : `Upload ${requiredPhotos.length - uploadedRequired.length} more required photos`}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Dual AI analysis will identify and mark all issues found
              </p>
            </div>
            <Button
              onClick={handleAnalyze}
              disabled={!canAnalyze || isAnalyzing}
              className="bg-slate-900 text-white rounded-sm hover:bg-slate-800 uppercase tracking-wider font-bold text-sm px-8 py-4"
            >
              {isAnalyzing ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <ImageIcon size={16} className="mr-2" />
                  Run AI Analysis
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {result && <AnalysisResultCard result={result} type={type} />}
    </div>
  );
}

// Vehicle Identifier Tab Component
function VehicleIdentifierTab() {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState({});
  const [previews, setPreviews] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const SLOTS = PHOTO_CATEGORIES.identify;

  const handlePhoto = (slotId, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Max 10MB per photo.");
      return;
    }
    setPhotos(prev => ({ ...prev, [slotId]: file }));
    const reader = new FileReader();
    reader.onload = (e) => setPreviews(prev => ({ ...prev, [slotId]: e.target.result }));
    reader.readAsDataURL(file);
  };

  const removePhoto = (slotId) => {
    setPhotos(prev => { const n = { ...prev }; delete n[slotId]; return n; });
    setPreviews(prev => { const n = { ...prev }; delete n[slotId]; return n; });
  };

  const photoCount = Object.keys(photos).length;
  const requiredCount = SLOTS.filter(s => s.required).length;
  const requiredUploaded = SLOTS.filter(s => s.required && photos[s.id]).length;
  const canSubmit = requiredUploaded >= requiredCount;

  const handleIdentify = async () => {
    if (!canSubmit) {
      toast.error("Please upload all required photos");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const formData = new FormData();
      Object.entries(photos).forEach(([key, file]) => {
        formData.append(`photo_${key}`, file);
      });
      const response = await axios.post(`${BASE}/api/v1/vehicle-identify`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      if (response.data.status === "success") {
        setResult(response.data.result);
        toast.success("Vehicle identified successfully!");
      } else {
        toast.error(response.data.error || "Identification failed");
      }
    } catch (err) {
      toast.error("Failed to identify vehicle. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resetAll = () => {
    setPhotos({});
    setPreviews({});
    setResult(null);
  };

  const confColor = (c) => c === "high" ? "text-emerald-600" : c === "medium" ? "text-amber-600" : "text-slate-400";
  const confBg = (c) => c === "high" ? "bg-emerald-50 border-emerald-200" : c === "medium" ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200";

  return (
    <div className="space-y-6" data-testid="vehicle-identifier-tab">
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-lg flex items-center justify-center bg-violet-100">
          <Search size={28} className="text-violet-600" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-slate-900">Vehicle Identifier</h2>
          <p className="text-base text-slate-500 mt-1">Upload car photos to identify make, model, fuel type & estimated year using AI</p>
        </div>
        <span className="text-sm text-slate-400">{photoCount}/4 Photos</span>
      </div>

      {/* Disclaimer */}
      <Card className="border border-violet-200 bg-violet-50/50 shadow-none rounded-sm">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info size={18} className="text-violet-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-bold text-violet-800">Tips for Best Identification</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 mt-2">
                {ANALYSIS_TYPES.identify.disclaimer.tips.map((tip, i) => (
                  <p key={i} className="text-xs text-violet-600 flex items-start gap-1.5">
                    <CheckCircle size={11} className="flex-shrink-0 mt-0.5" />{tip}
                  </p>
                ))}
              </div>
              <div className="mt-2 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                <p className="text-xs text-amber-700 flex items-start gap-1.5">
                  <AlertTriangle size={11} className="flex-shrink-0 mt-0.5" />
                  {ANALYSIS_TYPES.identify.disclaimer.warning}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload Grid */}
      <div className="grid grid-cols-4 gap-4">
        {SLOTS.map((slot) => (
          <Card key={slot.id} className="border border-dashed border-slate-300 shadow-none rounded-sm overflow-hidden" data-testid={`upload-slot-${slot.id}`}>
            <CardContent className="p-0">
              {previews[slot.id] ? (
                <div className="relative group">
                  <img src={previews[slot.id]} alt={slot.label} className="w-full h-40 object-cover" />
                  <button
                    onClick={() => removePhoto(slot.id)}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={14} />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-2 py-1">
                    <p className="text-[11px] text-white truncate">{slot.label}</p>
                  </div>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center h-40 cursor-pointer hover:bg-slate-50 transition-colors">
                  <Camera size={24} className="text-slate-300 mb-2" />
                  <p className="text-xs text-slate-500 font-medium text-center px-2">{slot.label}</p>
                  {slot.required && <span className="text-[10px] text-red-400 font-bold mt-1">Required</span>}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePhoto(slot.id, e.target.files[0])} />
                </label>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Identify Button */}
      <div className="flex items-center gap-3">
        <Button
          onClick={handleIdentify}
          disabled={!canSubmit || loading}
          className="bg-violet-600 hover:bg-violet-700 text-white px-8 py-3 font-bold uppercase tracking-wider"
          data-testid="identify-btn"
        >
          {loading ? (
            <><Loader2 size={18} className="mr-2 animate-spin" />Identifying...</>
          ) : (
            <><Search size={18} className="mr-2" />Identify Vehicle</>
          )}
        </Button>
        {result && (
          <Button variant="outline" onClick={resetAll} className="font-bold uppercase tracking-wider" data-testid="reset-btn">
            <Trash2 size={16} className="mr-2" />New Identification
          </Button>
        )}
      </div>

      {/* Results */}
      {result && (
        <Card className="border-2 border-violet-200 shadow-lg rounded-sm overflow-hidden" data-testid="vehicle-id-result">
          <div className="bg-violet-600 px-6 py-4">
            <h3 className="text-xl font-bold text-white">Vehicle Identification Result</h3>
          </div>
          <CardContent className="p-6 space-y-5">
            {/* Main ID */}
            <div className="flex items-start gap-6">
              <div className="flex-1">
                <p className="text-sm text-slate-500 uppercase tracking-wider font-bold">Identified Vehicle</p>
                <h2 className="text-3xl font-black text-slate-900 mt-1">
                  {result.manufacturer} {result.model}
                </h2>
                {result.variant && result.variant !== "null" && (
                  <p className="text-lg text-violet-600 font-semibold">{result.variant}</p>
                )}
                <p className="text-base text-slate-500 mt-1">
                  {result.body_type} {result.color ? `| ${result.color}` : ""} {result.country_of_origin ? `| Made in ${result.country_of_origin}` : ""}
                </p>
              </div>
            </div>

            {/* Detail Grid */}
            <div className="grid grid-cols-4 gap-4">
              <div className={`rounded-lg p-4 border ${confBg(result.manufacturer_confidence)}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Tag size={16} className="text-slate-600" />
                  <span className="text-xs font-bold uppercase text-slate-500">Manufacturer</span>
                </div>
                <p className="text-lg font-bold text-slate-900">{result.manufacturer}</p>
                <p className={`text-xs font-bold uppercase ${confColor(result.manufacturer_confidence)}`}>
                  {result.manufacturer_confidence} confidence
                </p>
              </div>

              <div className={`rounded-lg p-4 border ${confBg(result.model_confidence)}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Car size={16} className="text-slate-600" />
                  <span className="text-xs font-bold uppercase text-slate-500">Model</span>
                </div>
                <p className="text-lg font-bold text-slate-900">{result.model}</p>
                <p className={`text-xs font-bold uppercase ${confColor(result.model_confidence)}`}>
                  {result.model_confidence} confidence
                </p>
              </div>

              <div className={`rounded-lg p-4 border ${confBg(result.fuel_confidence)}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Fuel size={16} className="text-slate-600" />
                  <span className="text-xs font-bold uppercase text-slate-500">Fuel Type</span>
                </div>
                <p className="text-lg font-bold text-slate-900">{result.fuel_type}</p>
                <p className={`text-xs font-bold uppercase ${confColor(result.fuel_confidence)}`}>
                  {result.fuel_confidence} confidence
                </p>
              </div>

              <div className={`rounded-lg p-4 border ${confBg(result.year_confidence)}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Calendar size={16} className="text-slate-600" />
                  <span className="text-xs font-bold uppercase text-slate-500">Est. Year</span>
                </div>
                <p className="text-lg font-bold text-slate-900">{result.estimated_year_range}</p>
                <p className={`text-xs font-bold uppercase ${confColor(result.year_confidence)}`}>
                  {result.year_confidence} confidence
                </p>
              </div>
            </div>

            {/* Evidence & Details */}
            <div className="grid grid-cols-2 gap-4">
              {result.fuel_evidence && (
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
                  <p className="text-xs font-bold uppercase text-slate-500 mb-1">Fuel Type Evidence</p>
                  <p className="text-sm text-slate-700">{result.fuel_evidence}</p>
                </div>
              )}
              {result.generation && (
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
                  <p className="text-xs font-bold uppercase text-slate-500 mb-1">Generation</p>
                  <p className="text-sm text-slate-700">{result.generation}</p>
                </div>
              )}
              {result.key_identifiers && (
                <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 col-span-2">
                  <p className="text-xs font-bold uppercase text-blue-500 mb-1">How AI Identified This Vehicle</p>
                  <p className="text-sm text-blue-800">{result.key_identifiers}</p>
                </div>
              )}
            </div>

            {/* Badges detected */}
            {result.badges_detected && result.badges_detected.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold uppercase text-slate-500">Badges Detected:</span>
                {result.badges_detected.map((badge, i) => (
                  <Badge key={i} variant="outline" className="text-xs bg-white">{badge}</Badge>
                ))}
              </div>
            )}

            {result.additional_notes && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                <p className="text-xs text-amber-700"><span className="font-bold">Note:</span> {result.additional_notes}</p>
              </div>
            )}

            {/* Auto-fill Sound AI */}
            <div className="pt-2 border-t border-slate-200">
              <Button
                onClick={() => {
                  // Parse year from range (take first year)
                  let year = "";
                  if (result.estimated_year_range) {
                    const match = result.estimated_year_range.match(/(\d{4})/);
                    if (match) year = parseInt(match[1]);
                  }
                  navigate("/", {
                    state: {
                      prefill: {
                        make: result.manufacturer || "",
                        model: result.model || "",
                        year: year,
                        fuel_type: result.fuel_type || "",
                        variant: result.variant || "",
                      },
                      fromVehicleId: true,
                    }
                  });
                  toast.success("Navigating to Sound AI with vehicle details pre-filled");
                }}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white py-3 font-bold uppercase tracking-wider"
                data-testid="use-in-sound-ai-btn"
              >
                <ChevronRight size={18} className="mr-2" />
                Use in Sound AI — Auto-fill Form
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function VisionAIPage() {
  const [activeTab, setActiveTab] = useState("dents");

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase font-['Barlow'] text-slate-900">
          Vision AI Analysis
        </h1>
        <p className="text-base text-slate-500 mt-1">
          Upload car photos for AI-powered visual inspection and damage detection
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-100 rounded-sm h-12 p-1 grid grid-cols-4 w-full">
          <TabsTrigger 
            value="dents" 
            className="text-sm font-bold uppercase tracking-wider rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <Car size={16} className="mr-2" />
            Dents & Scratches
          </TabsTrigger>
          <TabsTrigger 
            value="accident" 
            className="text-sm font-bold uppercase tracking-wider rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <Shield size={16} className="mr-2" />
            Accident Analysis
          </TabsTrigger>
          <TabsTrigger 
            value="identify" 
            className="text-sm font-bold uppercase tracking-wider rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <Search size={16} className="mr-2" />
            Vehicle ID
          </TabsTrigger>
          <TabsTrigger 
            value="verification" 
            className="text-sm font-bold uppercase tracking-wider rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <Settings size={16} className="mr-2" />
            Verification
          </TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <TabsContent value="dents" className="mt-0">
            <VisionAnalysisTab type="dents" />
          </TabsContent>
          <TabsContent value="accident" className="mt-0">
            <VisionAnalysisTab type="accident" />
          </TabsContent>
          <TabsContent value="identify" className="mt-0">
            <VehicleIdentifierTab />
          </TabsContent>
          <TabsContent value="verification" className="mt-0">
            <VisionAnalysisTab type="verification" />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
